---
title: Connect a persona to its knowledge
summary: Point a persona at the right knowledge base using Graph ID, server URL, and model settings.
visibility: authenticated
audience: author-admin
section: How-to
order: 50
last_verified: 2026-07-21
owner: docs
---

# Connect a persona to its knowledge

A persona retrieves its answers from a knowledge base served by a RAG server. You connect
the two in the **AI Settings** tab of **Persona Settings**. On the persona's card, open
the **⋯** menu and choose **Settings…**, then go to **AI Settings**.

## Choose the knowledge graph

The **Graph ID** selects which knowledge graph (document corpus) the persona retrieves
from. Set it to the graph that holds this persona's documents.

- A Graph ID is a short, lowercase slug (letters, digits, and hyphens; up to 64
  characters), for example `patient-ed` or `clinical-anatomy`.
- Leave it empty to use the deployment's default graph.

## Point at the right RAG server

Most deployments serve RAG from a single place, and you can leave the **RAG Server URL**
on its default — the persona then uses the deployment's configured server automatically.
Set it only when this persona needs a different server. The field accepts three shapes:

- **A same-environment instance**, like `/rag-proxy/<instance>`.
- **A registered cross-environment alias**, like `/rag-external/<alias>` — these are set
  up by an administrator so browsers stay same-origin.
- **A full `https://` URL** — only available with the *override server URL* permission,
  and only when the target server allows this app's origin.

If the server needs its own key, set the optional **RAG Server API Key**.

## Pick the model (optional)

**Model** chooses the completion model. Leave it on the server default unless you have a
reason to override it. The dropdown is populated from the RAG server; use the refresh
button if it looks stale.

## Save and test

Click **Save Changes**, then start a conversation and ask something only the documents
would know. You should see an answer with **source citations**. If you get a generic
answer with no citations, check that the **Graph ID** points at a graph that actually
contains the relevant documents.

## Next

- Make answers stick to the sources: [Answer only from documents](/help/how-to/answer-only-from-documents).
- Full field list: [Persona configuration reference](/help/reference/persona-config).
